import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Button, Modal, ListGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ErrorUnexpected,
  OperationSuccessfully,
} from "../../../const/Messages";
import * as Yup from "yup";
import * as ElementTypeService from "../../../service/ElementTypeService";
import * as NeLogsFileService from "../../../service/NeLogsFileService";
import * as VendorService from "../../../service/VendorService";

const initialValues = {
  elementType: "",
  vendor: "",
  files: [],
};

/**
 * Criação:
 * @author Guilherme Almeida
 * @date 15/02/2021
 *
 * Atualização:
 * @author Thiago Guimarães
 * @date 01/09/2021
 *
 */
export default function LogBatchUploadModal({ show, setShow }) {
  const [vendorList, setVendorList] = useState([]);
  const [elementTypeList, setElementTypeList] = useState([]);

  const [stagedFiles, setStagedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const formikRef = useRef(null);

  useEffect(() => {
    if (show) {
      VendorService.findBatchSupported()
        .then((vendors) => {
          const activeAndSortedVendors = vendors
            .filter((v) => v.active === undefined || v.active === true)
            .sort((a, b) => a.name.localeCompare(b.name));
          setVendorList(activeAndSortedVendors);
        })
        .catch(() => toast.error(ErrorUnexpected));
    } else {
      setStagedFiles([]);
    }
  }, [show]);

  const schema = Yup.object().shape({
    elementType: Yup.number().required("Element Type is a required field"),
    vendor: Yup.number().required("Vendor is a required field"),
    files: Yup.array().min(1, "At least one file is required"),
  });

  function handleCloseModal(success = false) {
    if (!success) setStagedFiles([]);
    setShow(false);
  }

  function updateElementTypeCombo(vendorId) {
    setElementTypeList([]);
    formikRef.current.setFieldValue("elementType", "");

    if (vendorId) {
      ElementTypeService.getBatchByVendor(vendorId)
        .then((elementTypes) => {
          const activeAndSortedElementTypes = elementTypes
            .filter((et) => et.active === undefined || et.active === true)
            .sort((a, b) => a.name.localeCompare(b.name));
          setElementTypeList(activeAndSortedElementTypes);
        })
        .catch(() => toast.error(ErrorUnexpected));
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, setFieldValue) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(Array.from(e.dataTransfer.files), setFieldValue);
  };

  const handleFileSelect = (e, setFieldValue) => {
    processFiles(Array.from(e.target.files), setFieldValue);
    e.target.value = null;
  };

  const processFiles = (incomingFiles, setFieldValue) => {
    const newValidFiles = [];
    const currentFileNames = stagedFiles.map((f) => f.name);
    const batchFileNames = [];

    incomingFiles.forEach((file) => {
      if (
        !file.name.toLowerCase().endsWith(".txt") &&
        !file.name.toLowerCase().endsWith(".log")
      ) {
        toast.error(`Invalid format: ${file.name}`);
        return;
      }

      if (
        currentFileNames.includes(file.name) ||
        batchFileNames.includes(file.name)
      ) {
        toast.warning(`File ${file.name} is duplicated and was ignored.`);
      } else {
        newValidFiles.push(file);
        batchFileNames.push(file.name);
      }
    });

    if (newValidFiles.length > 0) {
      setStagedFiles((prevFiles) => {
        const updatedList = [...prevFiles, ...newValidFiles];
        setFieldValue("files", updatedList);
        return updatedList;
      });
    }
  };

  const removeFile = (fileName, setFieldValue) => {
    setStagedFiles((prev) => {
      const updatedList = prev.filter((f) => f.name !== fileName);
      setFieldValue("files", updatedList);
      return updatedList;
    });
  };

  function handleUpload(values, actions) {
    const formData = new FormData();
    formData.append("vendor", values.vendor);
    formData.append("elementType", values.elementType);

    values.files.forEach((file) => {
      formData.append("files", file);
    });

    NeLogsFileService.uploadBatch(formData)
      .then(() => {
        toast.success(OperationSuccessfully);
        handleCloseModal(true);
      })
      .catch(() => toast.error(ErrorUnexpected))
      .finally(() => actions.setSubmitting(false));
  }

  return (
    <Modal
      show={show}
      onHide={() => handleCloseModal(false)}
      size="lg"
      animation={false}
    >
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        validationSchema={schema}
        onSubmit={handleUpload}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Modal.Header closeButton>
              <Modal.Title>File - Batch Upload</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <div className="form-group row">
                <label htmlFor="vendor" className="col-sm-3 col-form-label">
                  Vendor:
                </label>
                <div className="col-sm-9">
                  <Field
                    as="select"
                    name="vendor"
                    className="form-control"
                    value={values.vendor}
                    onChange={(ev) => {
                      setFieldValue("vendor", ev.target.value);
                      updateElementTypeCombo(ev.target.value);
                    }}
                  >
                    <option value="">-- Select Option --</option>
                    {vendorList.map((vendor) => (
                      <option value={vendor.id} key={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </Field>
                  <span className="text-danger">
                    <ErrorMessage name="vendor" />
                  </span>
                </div>
              </div>

              <div className="form-group row">
                <label
                  htmlFor="elementType"
                  className="col-sm-3 col-form-label"
                >
                  Element Type:
                </label>
                <div className="col-sm-9">
                  <Field
                    as="select"
                    name="elementType"
                    className="form-control"
                    value={values.elementType}
                    onChange={(ev) => {
                      setFieldValue("elementType", ev.target.value);
                    }}
                  >
                    <option value="">-- Select Option --</option>
                    {elementTypeList.map((elementType) => (
                      <option value={elementType.id} key={elementType.id}>
                        {elementType.name}
                      </option>
                    ))}
                  </Field>
                  <span className="text-danger">
                    <ErrorMessage name="elementType" />
                  </span>
                </div>
              </div>

              <hr />

              <div className="form-group">
                <label className="font-weight-bold">Files:</label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, setFieldValue)}
                  style={{
                    border: isDragging
                      ? "2px dashed #007bff"
                      : "2px dashed #ccc",
                    backgroundColor: isDragging ? "#f8fbff" : "#fdfdfd",
                    padding: "30px",
                    textAlign: "center",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() =>
                    document.getElementById("batchFileInput").click()
                  }
                >
                  <h6 className="text-muted mb-1">
                    {isDragging
                      ? "Drop files here!"
                      : "Drag your files here or click to select"}
                  </h6>
                  <small className="text-muted">
                    Supported formats: .log, .txt
                  </small>

                  <input
                    type="file"
                    id="batchFileInput"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleFileSelect(e, setFieldValue)}
                    accept=".txt,.log"
                  />
                </div>
                <span className="text-danger">
                  <ErrorMessage name="files" />
                </span>
              </div>

              {stagedFiles.length > 0 && (
                <div className="mt-3">
                  <ListGroup style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {stagedFiles.map((file, index) => (
                      <ListGroup.Item
                        key={index}
                        className="d-flex justify-content-between align-items-center py-2"
                      >
                        <div>
                          <strong className="text-primary">
                            {file.name}
                          </strong>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFile(file.name, setFieldValue)}
                        >
                          X
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="mt-3 mr-4 d-flex justify-content-end w-100">
                <Button
                  className="mr-2"
                  onClick={() => handleCloseModal(false)}
                  variant="secondary"
                  type="button"
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || values.files.length === 0}
                >
                  {values.files.length === 0
                    ? "Upload"
                    : `Upload (${values.files.length} file${
                        values.files.length > 1 ? "s" : ""
                      })`}
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
